import React, { useCallback, useEffect, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react/component-with-fg';
import { ButtonMenuItem } from '@atlaskit/side-nav-items/button-menu-item';
import {
	ExpandableMenuItem,
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
} from '@atlaskit/side-nav-items/flyout-menu-item';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { MenuListItem } from '@atlaskit/side-nav-items/menu-list-item';
import { L1_MENU_GROUPS } from '@atlassian/navigation-common';
import type {
	ButtonItemConfig,
	CustomItemConfig,
	ExpandableItemConfig,
	FlyoutItemConfig,
	LinkItemConfig,
	ObjectListFlyoutItemConfig,
	ObjectListFlyoutProps,
} from '@atlassian/navigation-common/types';
import {
	type ItemDragAndDropGroup,
	useL1ItemDragAndDrop,
} from '@atlassian/navigation-drag-and-drop';

import { useOptions } from '../../../controllers/options-provider';
import { ObjectListFlyout } from '../../../ui/object-list-flyout';
import { withSideNavigationAnalyticsListener } from '../../analytics/context';
import type { NavItemConfigInternal } from '../../types';
import {
	ElementEventListener,
	ElementEventListenerProvider,
} from '../../utils/element-event-listener';
import { omitSafely } from '../../utils/omit-safely';
import {
	isButtonItemConfig,
	isCustomItemConfig,
	isExpandableItemConfig,
	isFlyoutItemConfig,
	isLinkItemConfig,
	isObjectListFlyoutItemConfig,
} from '../../utils/type-guards';
import { useNavAnalytics } from '../../utils/use-nav-analytics';

import { useExpandOpenState } from './use-expand-open-state';

export interface NavItemProps {
	group?: ItemDragAndDropGroup;
	id: string;
	isInMoreMenu?: boolean;
	item: NavItemConfigInternal;
	onClick?: () => void;
	// only used for custom items
	onPeek?: () => void;
	peek?: boolean;
}

// Gated behind `navx-4728-jira-stability-fixes`: gate ON renders the refactored
// `NavItemRawStable`, which splits each item type into its own memoised leaf
// component and hands each leaf a referentially-stable `onClick` so the
// memoised `LinkMenuItem` / `ButtonMenuItem` / trigger leaves are not handed a
// brand-new handler on every `NavItem` render (which would defeat their memo).
// Gate OFF renders the original `NavItemRaw`, preserving current behaviour.
const GatedNavItem = componentWithFG(
	'navx-4728-jira-stability-fixes',
	NavItemRawStable,
	NavItemRaw,
);

export const NavItem: React.NamedExoticComponent<NavItemProps> = React.memo(GatedNavItem);
export default NavItem;

function NavItemRaw({
	id,
	item,
	peek = false,
	isInMoreMenu = false,
	onClick,
	onPeek,
	group = L1_MENU_GROUPS.MENU,
}: NavItemProps): React.ReactNode {
	const { l1DragAndDropEnabled } = useOptions();
	const { type } = item;

	const ItemWrapper = item.ItemWrapper ?? React.Fragment;

	const { isExpandOrFlyoutOpen, onExpandFlyoutToggle, isExpandable } = useExpandOpenState(
		item,
		peek,
	);
	const {
		draggableRef,
		isItemDraggable,
		isDragging,
		dropTargetRef,
		dropIndicator,
		maybeDragPreview,
	} = useL1ItemDragAndDrop({
		item,
		id,
		isExpandable,
		isExpandOrFlyoutOpen,
		onExpandFlyoutToggle,
		peek,
		group,
		l1DragAndDropEnabled,
	});

	const {
		fireClickedSideNavItemEvent,
		fireClickedSideNavSubMenuItemEvent,
		fireViewedSideNavSubMenuEvent,
	} = useNavAnalytics();

	// Common internal onClick handler for all top-level item types
	const onNavItemClick = useCallback(
		(isRightClick?: boolean, status?: 'expanded' | 'collapsed') => {
			fg('navx-global-side-nav-confluence')
				? fireClickedSideNavItemEvent(id, undefined, isRightClick, status)
				: fireClickedSideNavItemEvent(id, undefined, isRightClick);
		},
		[fireClickedSideNavItemEvent, id],
	);

	// Common onSubMenuItemClick handler for all sub-menu item types
	const onSubMenuItemClick = useCallback(
		(isRightClick?: boolean, extendedAttributes?: Record<string, unknown>) => {
			fireClickedSideNavSubMenuItemEvent(
				id,
				undefined,
				isInMoreMenu,
				isRightClick,
				undefined,
				extendedAttributes ?? null,
			);
		},
		[fireClickedSideNavSubMenuItemEvent, id, isInMoreMenu],
	);

	useEffect(() => {
		if (isExpandable && isExpandOrFlyoutOpen) {
			fireViewedSideNavSubMenuEvent(id, undefined, isInMoreMenu);
		}
	}, [fireViewedSideNavSubMenuEvent, id, isExpandOrFlyoutOpen, isExpandable, isInMoreMenu, type]);

	if (isCustomItemConfig(item)) {
		const Wrapper = item.isMenuListItem ? React.Fragment : MenuListItem;
		// If the item is a custom item, render it directly
		// TODO: Add customizationHandlers prop when ready.
		return (
			<ItemWrapper>
				<Wrapper>
					<item.Component
						draggableRef={draggableRef}
						dropIndicator={dropIndicator}
						hasDragIndicator={isItemDraggable}
						isDragging={isDragging}
						isExpandOrFlyoutOpen={isExpandOrFlyoutOpen}
						isInMoreMenu={isInMoreMenu && !peek}
						isPeeked={peek}
						onClick={(...args) => {
							item.Component.defaultProps?.onClick?.(...args);
							fireClickedSideNavItemEvent(id);
							onClick?.();
						}}
						onExpandFlyoutToggle={onExpandFlyoutToggle}
						onPeek={onPeek}
						visualContentRef={dropTargetRef}
					/>

					{maybeDragPreview}
				</Wrapper>
			</ItemWrapper>
		);
	} else if (isLinkItemConfig(item)) {
		const { label, labelElem, ...linkMenuItemProps } = omitSafely(item, 'type');

		return (
			<ItemWrapper>
				<ElementEventListener handler={onNavItemClick}>
					<LinkMenuItem
						{...linkMenuItemProps}
						onClick={(...args) => {
							linkMenuItemProps.onClick?.(...args);
							onNavItemClick();
							onClick?.();
						}}
						hasDragIndicator={isItemDraggable}
						ref={draggableRef}
						isDragging={isDragging}
						dropIndicator={dropIndicator}
						visualContentRef={dropTargetRef}
						actionsOnHover={getActionsOnHover({ id, isItemDraggable, item, isInMoreMenu, group })}
					>
						{labelElem ?? label}
					</LinkMenuItem>
					{maybeDragPreview}
				</ElementEventListener>
			</ItemWrapper>
		);
	} else if (isButtonItemConfig(item)) {
		const { label, labelElem, ...buttonMenuItemProps } = omitSafely(item, 'type');
		return (
			<ItemWrapper>
				<ButtonMenuItem
					{...buttonMenuItemProps}
					onClick={(...args) => {
						buttonMenuItemProps.onClick?.(...args);
						onNavItemClick();
						onClick?.();
					}}
					hasDragIndicator={isItemDraggable}
					ref={draggableRef}
					isDragging={isDragging}
					dropIndicator={dropIndicator}
					visualContentRef={dropTargetRef}
					actionsOnHover={getActionsOnHover({ id, isItemDraggable, item, isInMoreMenu, group })}
				>
					{labelElem ?? label}
				</ButtonMenuItem>
				{maybeDragPreview}
			</ItemWrapper>
		);
	} else if (isExpandableItemConfig(item)) {
		// eslint-disable-next-line no-unused-vars
		const { children, elemBefore, label, labelElem, ...triggerProps } = omitSafely(
			item,
			'type',
			'isDefaultExpanded', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
			'isExpanded', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
			'onExpansionToggle', // Handled above with onExpandFlyoutToggle - listed here so it doesn't go in triggerProps
		);

		// Wrap children with our analytics listener to capture their click events
		const wrappedChildren = withSideNavigationAnalyticsListener(item.children, onSubMenuItemClick);

		return (
			<ItemWrapper>
				<ExpandableMenuItem
					dropIndicator={dropIndicator}
					isExpanded={isExpandOrFlyoutOpen}
					onExpansionToggle={onExpandFlyoutToggle}
					ref={dropTargetRef}
				>
					<ExpandableMenuItemTrigger
						{...triggerProps}
						onClick={(...args) => {
							triggerProps.onClick?.(...args);
							fg('navx-global-side-nav-confluence')
								? onNavItemClick(false, isExpandOrFlyoutOpen ? 'collapsed' : 'expanded')
								: onNavItemClick();
							onClick?.();
						}}
						// During drags, custom elemBefore is not shown
						// https://atlassian.design/components/navigation-system/side-nav-items/drag-and-drop#drag-start
						elemBefore={isDragging ? null : elemBefore}
						hasDragIndicator={isItemDraggable}
						ref={draggableRef}
						isDragging={isDragging}
						actionsOnHover={getActionsOnHover({ id, isItemDraggable, item, isInMoreMenu, group })}
					>
						{labelElem ?? label}
					</ExpandableMenuItemTrigger>
					<ExpandableMenuItemContent>{wrappedChildren}</ExpandableMenuItemContent>
				</ExpandableMenuItem>
				{maybeDragPreview}
			</ItemWrapper>
		);
	} else if (isFlyoutItemConfig(item)) {
		// eslint-disable-next-line no-unused-vars
		const { children, label, labelElem, ...triggerProps } = omitSafely(
			item,
			'type',
			'isDefaultOpen', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
			'isOpen', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
			'onOpenChange', // Handled above with onExpandFlyoutToggle - listed here so it doesn't go in triggerProps
		);

		// Wrap children with our analytics listener to capture their click events
		const wrappedChildren = withSideNavigationAnalyticsListener(item.children, onSubMenuItemClick);

		return (
			<ItemWrapper>
				<FlyoutMenuItem isOpen={isExpandOrFlyoutOpen}>
					<FlyoutMenuItemTrigger
						{...triggerProps}
						onClick={(...args) => {
							triggerProps.onClick?.(...args);
							// Toggle the flyout open state
							onExpandFlyoutToggle(!isExpandOrFlyoutOpen);
							fg('navx-global-side-nav-confluence')
								? onNavItemClick(false, isExpandOrFlyoutOpen ? 'collapsed' : 'expanded')
								: onNavItemClick();
							onClick?.();
						}}
						hasDragIndicator={isItemDraggable}
						ref={draggableRef}
						isDragging={isDragging}
						dropIndicator={dropIndicator}
						visualContentRef={dropTargetRef}
					>
						{labelElem ?? label}
					</FlyoutMenuItemTrigger>
					<FlyoutMenuItemContent onClose={() => onExpandFlyoutToggle(false)}>
						{wrappedChildren}
					</FlyoutMenuItemContent>
				</FlyoutMenuItem>
				{maybeDragPreview}
			</ItemWrapper>
		);
	} else if (isObjectListFlyoutItemConfig(item)) {
		const objectListFlyoutProps = omitSafely(
			item,
			'type',
			'isOpen', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in objectListFlyoutProps
			'onOpenChange', // Handled above with onExpandFlyoutToggle - listed here so it doesn't go in objectListFlyoutProps
		);

		const isOpenExternallyControlled = typeof item.isOpen === 'boolean';
		// We only want to control the flyout if it is already open, or when we are doing a drag operation.
		// In other cases, we should return undefined to signal that the OLF is not controlled.
		const isControlledFlyout = isOpenExternallyControlled
			? item.isOpen
			: isExpandOrFlyoutOpen || isDragging
				? isExpandOrFlyoutOpen
				: undefined;

		return (
			<ItemWrapper>
				<ElementEventListenerProvider handler={onSubMenuItemClick}>
					<ObjectListFlyout
						{...objectListFlyoutProps}
						itemMenuId={id}
						onClick={(...args) => {
							// Toggle the flyout open state
							onExpandFlyoutToggle(!isExpandOrFlyoutOpen);
							(objectListFlyoutProps as ObjectListFlyoutProps).onClick?.(...args);
							fg('navx-global-side-nav-confluence')
								? onNavItemClick(false, isExpandOrFlyoutOpen ? 'collapsed' : 'expanded')
								: onNavItemClick();
							onClick?.();
						}}
						onSubMenuItemClick={(event, extendedSubItemAnalyticsAttributes) => {
							(objectListFlyoutProps as ObjectListFlyoutProps).onSubMenuItemClick?.(
								event,
								extendedSubItemAnalyticsAttributes,
							);
							onSubMenuItemClick(false, extendedSubItemAnalyticsAttributes);
						}}
						isFlyoutOpen={isControlledFlyout}
						onOpenChange={onExpandFlyoutToggle}
						hasDragIndicator={isItemDraggable}
						triggerRef={draggableRef}
						isDragging={isDragging}
						dropIndicator={dropIndicator}
						visualContentRef={dropTargetRef}
					/>
					{maybeDragPreview}
				</ElementEventListenerProvider>
			</ItemWrapper>
		);
	} else {
		return null;
	}
}

/**
 * Shared, already-computed values that the dispatcher (`NavItemRawStable`) hands
 * to every leaf component. Keeping the hooks in the dispatcher means they run
 * unconditionally (rules-of-hooks safe), while each leaf only adds its own
 * stable `onClick` handler.
 */
type SharedLeafProps = {
	draggableRef: ReturnType<typeof useL1ItemDragAndDrop>['draggableRef'];
	dropIndicator: ReturnType<typeof useL1ItemDragAndDrop>['dropIndicator'];
	dropTargetRef: ReturnType<typeof useL1ItemDragAndDrop>['dropTargetRef'];
	fireClickedSideNavItemEvent: (id: string) => void;
	group: ItemDragAndDropGroup;
	id: string;
	isDragging: boolean;
	isExpandOrFlyoutOpen: boolean;
	isInMoreMenu: boolean;
	isItemDraggable: boolean;
	ItemWrapper: NonNullable<NavItemConfigInternal['ItemWrapper']> | typeof React.Fragment;
	maybeDragPreview: ReturnType<typeof useL1ItemDragAndDrop>['maybeDragPreview'];
	onClick?: () => void;
	onExpandFlyoutToggle: (open: boolean) => void;
	onNavItemClick: (isRightClick?: boolean, status?: 'expanded' | 'collapsed') => void;
	onPeek?: () => void;
	onSubMenuItemClick: (
		isRightClick?: boolean,
		extendedAttributes?: Record<string, unknown>,
	) => void;
	peek: boolean;
};

/**
 * Refactored dispatcher used when `navx-4728-jira-stability-fixes` is ON.
 *
 * All shared hooks run here once, then a thin type-guard switch selects which
 * memoised leaf component to render. The leaves receive the computed values via
 * `SharedLeafProps` and own their referentially-stable `onClick` handlers, so a
 * re-render of `NavItemRawStable` that does not change a leaf's props lets that
 * leaf bail out of re-rendering.
 */
function NavItemRawStable({
	id,
	item,
	peek = false,
	isInMoreMenu = false,
	onClick,
	onPeek,
	group = L1_MENU_GROUPS.MENU,
}: NavItemProps): React.ReactNode {
	const { l1DragAndDropEnabled } = useOptions();
	const { type } = item;

	const ItemWrapper = item.ItemWrapper ?? React.Fragment;

	const { isExpandOrFlyoutOpen, onExpandFlyoutToggle, isExpandable } = useExpandOpenState(
		item,
		peek,
	);
	const {
		draggableRef,
		isItemDraggable,
		isDragging,
		dropTargetRef,
		dropIndicator,
		maybeDragPreview,
	} = useL1ItemDragAndDrop({
		item,
		id,
		isExpandable,
		isExpandOrFlyoutOpen,
		onExpandFlyoutToggle,
		peek,
		group,
		l1DragAndDropEnabled,
	});

	const {
		fireClickedSideNavItemEvent,
		fireClickedSideNavSubMenuItemEvent,
		fireViewedSideNavSubMenuEvent,
	} = useNavAnalytics();

	// Common internal onClick handler for all top-level item types
	const onNavItemClick = useCallback(
		(isRightClick?: boolean, status?: 'expanded' | 'collapsed') => {
			fg('navx-global-side-nav-confluence')
				? fireClickedSideNavItemEvent(id, undefined, isRightClick, status)
				: fireClickedSideNavItemEvent(id, undefined, isRightClick);
		},
		[fireClickedSideNavItemEvent, id],
	);

	// Common onSubMenuItemClick handler for all sub-menu item types
	const onSubMenuItemClick = useCallback(
		(isRightClick?: boolean, extendedAttributes?: Record<string, unknown>) => {
			fireClickedSideNavSubMenuItemEvent(
				id,
				undefined,
				isInMoreMenu,
				isRightClick,
				undefined,
				extendedAttributes ?? null,
			);
		},
		[fireClickedSideNavSubMenuItemEvent, id, isInMoreMenu],
	);

	useEffect(() => {
		if (isExpandable && isExpandOrFlyoutOpen) {
			fireViewedSideNavSubMenuEvent(id, undefined, isInMoreMenu);
		}
	}, [fireViewedSideNavSubMenuEvent, id, isExpandOrFlyoutOpen, isExpandable, isInMoreMenu, type]);

	const shared: SharedLeafProps = {
		id,
		onClick,
		onPeek,
		peek,
		isInMoreMenu,
		group,
		ItemWrapper,
		draggableRef,
		dropTargetRef,
		dropIndicator,
		maybeDragPreview,
		isItemDraggable,
		isDragging,
		isExpandOrFlyoutOpen,
		onExpandFlyoutToggle,
		onNavItemClick,
		onSubMenuItemClick,
		fireClickedSideNavItemEvent,
	};

	if (isCustomItemConfig(item)) {
		return <CustomNavItem item={item} {...shared} />;
	} else if (isLinkItemConfig(item)) {
		return <LinkNavItem item={item} {...shared} />;
	} else if (isButtonItemConfig(item)) {
		return <ButtonNavItem item={item} {...shared} />;
	} else if (isExpandableItemConfig(item)) {
		return <ExpandableNavItem item={item} {...shared} />;
	} else if (isFlyoutItemConfig(item)) {
		return <FlyoutNavItem item={item} {...shared} />;
	} else if (isObjectListFlyoutItemConfig(item)) {
		return <ObjectListFlyoutNavItem item={item} {...shared} />;
	} else {
		return null;
	}
}

const CustomNavItem = React.memo(function CustomNavItem({
	item,
	onClick,
	onPeek,
	peek,
	isInMoreMenu,
	ItemWrapper,
	draggableRef,
	dropTargetRef,
	dropIndicator,
	maybeDragPreview,
	isItemDraggable,
	isDragging,
	isExpandOrFlyoutOpen,
	onExpandFlyoutToggle,
	fireClickedSideNavItemEvent,
	id,
}: SharedLeafProps & { item: CustomItemConfig }) {
	const Wrapper = item.isMenuListItem ? React.Fragment : MenuListItem;

	// Stable click handler. `customItemOnClickRef` carries the latest per-item
	// default onClick so the callback identity stays stable across renders.
	const customItemOnClickRef = useRef<((...args: any[]) => void) | undefined>(
		item.Component.defaultProps?.onClick,
	);
	customItemOnClickRef.current = item.Component.defaultProps?.onClick;
	const onCustomItemClick = useCallback(
		(...args: any[]) => {
			customItemOnClickRef.current?.(...args);
			fireClickedSideNavItemEvent(id);
			onClick?.();
		},
		[fireClickedSideNavItemEvent, id, onClick],
	);

	// TODO: Add customizationHandlers prop when ready.
	return (
		<ItemWrapper>
			<Wrapper>
				<item.Component
					draggableRef={draggableRef}
					dropIndicator={dropIndicator}
					hasDragIndicator={isItemDraggable}
					isDragging={isDragging}
					isExpandOrFlyoutOpen={isExpandOrFlyoutOpen}
					isInMoreMenu={isInMoreMenu && !peek}
					isPeeked={peek}
					onClick={onCustomItemClick}
					onExpandFlyoutToggle={onExpandFlyoutToggle}
					onPeek={onPeek}
					visualContentRef={dropTargetRef}
				/>
				{maybeDragPreview}
			</Wrapper>
		</ItemWrapper>
	);
});

const LinkNavItem = React.memo(function LinkNavItem({
	item,
	onClick,
	isInMoreMenu,
	group,
	ItemWrapper,
	draggableRef,
	dropTargetRef,
	dropIndicator,
	maybeDragPreview,
	isItemDraggable,
	isDragging,
	onNavItemClick,
	id,
}: SharedLeafProps & { item: LinkItemConfig }) {
	const { label, labelElem, ...linkMenuItemProps } = omitSafely(item, 'type');

	// Stable click handler. `itemOnClickRef` carries the latest per-item onClick.
	const itemOnClickRef = useRef<((...args: any[]) => void) | undefined>(linkMenuItemProps.onClick);
	itemOnClickRef.current = linkMenuItemProps.onClick;
	const onLinkItemClick = useCallback(
		(...args: any[]) => {
			itemOnClickRef.current?.(...args);
			onNavItemClick();
			onClick?.();
		},
		[onNavItemClick, onClick],
	);

	return (
		<ItemWrapper>
			<ElementEventListener handler={onNavItemClick}>
				<LinkMenuItem
					{...linkMenuItemProps}
					onClick={onLinkItemClick}
					hasDragIndicator={isItemDraggable}
					ref={draggableRef}
					isDragging={isDragging}
					dropIndicator={dropIndicator}
					visualContentRef={dropTargetRef}
					actionsOnHover={getActionsOnHover({ id, isItemDraggable, item, isInMoreMenu, group })}
				>
					{labelElem ?? label}
				</LinkMenuItem>
				{maybeDragPreview}
			</ElementEventListener>
		</ItemWrapper>
	);
});

const ButtonNavItem = React.memo(function ButtonNavItem({
	item,
	onClick,
	isInMoreMenu,
	group,
	ItemWrapper,
	draggableRef,
	dropTargetRef,
	dropIndicator,
	maybeDragPreview,
	isItemDraggable,
	isDragging,
	onNavItemClick,
	id,
}: SharedLeafProps & { item: ButtonItemConfig }) {
	const { label, labelElem, ...buttonMenuItemProps } = omitSafely(item, 'type');

	// Stable click handler. `itemOnClickRef` carries the latest per-item onClick.
	const itemOnClickRef = useRef<((...args: any[]) => void) | undefined>(
		buttonMenuItemProps.onClick,
	);
	itemOnClickRef.current = buttonMenuItemProps.onClick;
	const onButtonItemClick = useCallback(
		(...args: any[]) => {
			itemOnClickRef.current?.(...args);
			onNavItemClick();
			onClick?.();
		},
		[onNavItemClick, onClick],
	);

	return (
		<ItemWrapper>
			<ButtonMenuItem
				{...buttonMenuItemProps}
				onClick={onButtonItemClick}
				hasDragIndicator={isItemDraggable}
				ref={draggableRef}
				isDragging={isDragging}
				dropIndicator={dropIndicator}
				visualContentRef={dropTargetRef}
				actionsOnHover={getActionsOnHover({ id, isItemDraggable, item, isInMoreMenu, group })}
			>
				{labelElem ?? label}
			</ButtonMenuItem>
			{maybeDragPreview}
		</ItemWrapper>
	);
});

const ExpandableNavItem = React.memo(function ExpandableNavItem({
	item,
	onClick,
	isInMoreMenu,
	group,
	ItemWrapper,
	draggableRef,
	dropTargetRef,
	dropIndicator,
	maybeDragPreview,
	isItemDraggable,
	isDragging,
	isExpandOrFlyoutOpen,
	onExpandFlyoutToggle,
	onNavItemClick,
	onSubMenuItemClick,
	id,
}: SharedLeafProps & { item: ExpandableItemConfig }) {
	// eslint-disable-next-line no-unused-vars
	const { children, elemBefore, label, labelElem, ...triggerProps } = omitSafely(
		item,
		'type',
		'isDefaultExpanded', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
		'isExpanded', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
		'onExpansionToggle', // Handled above with onExpandFlyoutToggle - listed here so it doesn't go in triggerProps
	);

	// Wrap children with our analytics listener to capture their click events
	const wrappedChildren = withSideNavigationAnalyticsListener(item.children, onSubMenuItemClick);

	// Latest-ref for the open state so the stable handler reads the current value
	// without being recreated whenever it toggles.
	const isExpandOrFlyoutOpenRef = useRef(isExpandOrFlyoutOpen);
	isExpandOrFlyoutOpenRef.current = isExpandOrFlyoutOpen;

	// Stable click handler for the expandable trigger.
	const triggerOnClickRef = useRef<((...args: any[]) => void) | undefined>(triggerProps.onClick);
	triggerOnClickRef.current = triggerProps.onClick;
	const onTriggerClick = useCallback(
		(...args: any[]) => {
			triggerOnClickRef.current?.(...args);
			fg('navx-global-side-nav-confluence')
				? onNavItemClick(false, isExpandOrFlyoutOpenRef.current ? 'collapsed' : 'expanded')
				: onNavItemClick();
			onClick?.();
		},
		[onNavItemClick, onClick],
	);

	return (
		<ItemWrapper>
			<ExpandableMenuItem
				dropIndicator={dropIndicator}
				isExpanded={isExpandOrFlyoutOpen}
				onExpansionToggle={onExpandFlyoutToggle}
				ref={dropTargetRef}
			>
				<ExpandableMenuItemTrigger
					{...triggerProps}
					onClick={onTriggerClick}
					// During drags, custom elemBefore is not shown
					// https://atlassian.design/components/navigation-system/side-nav-items/drag-and-drop#drag-start
					elemBefore={isDragging ? null : elemBefore}
					hasDragIndicator={isItemDraggable}
					ref={draggableRef}
					isDragging={isDragging}
					actionsOnHover={getActionsOnHover({ id, isItemDraggable, item, isInMoreMenu, group })}
				>
					{labelElem ?? label}
				</ExpandableMenuItemTrigger>
				<ExpandableMenuItemContent>{wrappedChildren}</ExpandableMenuItemContent>
			</ExpandableMenuItem>
			{maybeDragPreview}
		</ItemWrapper>
	);
});

const FlyoutNavItem = React.memo(function FlyoutNavItem({
	item,
	onClick,
	ItemWrapper,
	draggableRef,
	dropTargetRef,
	dropIndicator,
	maybeDragPreview,
	isItemDraggable,
	isDragging,
	isExpandOrFlyoutOpen,
	onExpandFlyoutToggle,
	onNavItemClick,
	onSubMenuItemClick,
}: SharedLeafProps & { item: FlyoutItemConfig }) {
	// eslint-disable-next-line no-unused-vars
	const { children, label, labelElem, ...triggerProps } = omitSafely(
		item,
		'type',
		'isDefaultOpen', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
		'isOpen', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
		'onOpenChange', // Handled above with onExpandFlyoutToggle - listed here so it doesn't go in triggerProps
	);

	// Wrap children with our analytics listener to capture their click events
	const wrappedChildren = withSideNavigationAnalyticsListener(item.children, onSubMenuItemClick);

	const isExpandOrFlyoutOpenRef = useRef(isExpandOrFlyoutOpen);
	isExpandOrFlyoutOpenRef.current = isExpandOrFlyoutOpen;

	// Stable click handler for the flyout trigger.
	const triggerOnClickRef = useRef<((...args: any[]) => void) | undefined>(triggerProps.onClick);
	triggerOnClickRef.current = triggerProps.onClick;
	const onTriggerClick = useCallback(
		(...args: any[]) => {
			triggerOnClickRef.current?.(...args);
			// Toggle the flyout open state
			onExpandFlyoutToggle(!isExpandOrFlyoutOpenRef.current);
			fg('navx-global-side-nav-confluence')
				? onNavItemClick(false, isExpandOrFlyoutOpenRef.current ? 'collapsed' : 'expanded')
				: onNavItemClick();
			onClick?.();
		},
		[onExpandFlyoutToggle, onNavItemClick, onClick],
	);
	const onContentClose = useCallback(() => onExpandFlyoutToggle(false), [onExpandFlyoutToggle]);

	return (
		<ItemWrapper>
			<FlyoutMenuItem isOpen={isExpandOrFlyoutOpen}>
				<FlyoutMenuItemTrigger
					{...triggerProps}
					onClick={onTriggerClick}
					hasDragIndicator={isItemDraggable}
					ref={draggableRef}
					isDragging={isDragging}
					dropIndicator={dropIndicator}
					visualContentRef={dropTargetRef}
				>
					{labelElem ?? label}
				</FlyoutMenuItemTrigger>
				<FlyoutMenuItemContent onClose={onContentClose}>{wrappedChildren}</FlyoutMenuItemContent>
			</FlyoutMenuItem>
			{maybeDragPreview}
		</ItemWrapper>
	);
});

const ObjectListFlyoutNavItem = React.memo(function ObjectListFlyoutNavItem({
	item,
	onClick,
	ItemWrapper,
	draggableRef,
	dropTargetRef,
	dropIndicator,
	maybeDragPreview,
	isItemDraggable,
	isDragging,
	isExpandOrFlyoutOpen,
	onExpandFlyoutToggle,
	onNavItemClick,
	onSubMenuItemClick,
	id,
}: SharedLeafProps & { item: ObjectListFlyoutItemConfig }) {
	const objectListFlyoutProps = omitSafely(
		item,
		'type',
		'isOpen', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in objectListFlyoutProps
		'onOpenChange', // Handled above with onExpandFlyoutToggle - listed here so it doesn't go in objectListFlyoutProps
	);

	const isOpenExternallyControlled = typeof item.isOpen === 'boolean';
	// We only want to control the flyout if it is already open, or when we are doing a drag operation.
	// In other cases, we should return undefined to signal that the OLF is not controlled.
	const isControlledFlyout = isOpenExternallyControlled
		? item.isOpen
		: isExpandOrFlyoutOpen || isDragging
			? isExpandOrFlyoutOpen
			: undefined;

	const isExpandOrFlyoutOpenRef = useRef(isExpandOrFlyoutOpen);
	isExpandOrFlyoutOpenRef.current = isExpandOrFlyoutOpen;

	// Stable click handler for the object-list flyout trigger. Toggles the flyout
	// first (matching the original order), then per-item onClick + analytics.
	const flyoutOnClickRef = useRef<((...args: any[]) => void) | undefined>(
		(objectListFlyoutProps as ObjectListFlyoutProps).onClick,
	);
	flyoutOnClickRef.current = (objectListFlyoutProps as ObjectListFlyoutProps).onClick;
	const onObjectListFlyoutClick = useCallback(
		(...args: any[]) => {
			onExpandFlyoutToggle(!isExpandOrFlyoutOpenRef.current);
			flyoutOnClickRef.current?.(...args);
			fg('navx-global-side-nav-confluence')
				? onNavItemClick(false, isExpandOrFlyoutOpenRef.current ? 'collapsed' : 'expanded')
				: onNavItemClick();
			onClick?.();
		},
		[onExpandFlyoutToggle, onNavItemClick, onClick],
	);

	const subMenuItemOnClickRef = useRef<
		((event: any, extendedSubItemAnalyticsAttributes?: Record<string, unknown>) => void) | undefined
	>((objectListFlyoutProps as ObjectListFlyoutProps).onSubMenuItemClick);
	subMenuItemOnClickRef.current = (
		objectListFlyoutProps as ObjectListFlyoutProps
	).onSubMenuItemClick;
	const onObjectListSubMenuItemClick = useCallback(
		(event: any, extendedSubItemAnalyticsAttributes?: Record<string, unknown>) => {
			subMenuItemOnClickRef.current?.(event, extendedSubItemAnalyticsAttributes);
			onSubMenuItemClick(false, extendedSubItemAnalyticsAttributes);
		},
		[onSubMenuItemClick],
	);

	return (
		<ItemWrapper>
			<ElementEventListenerProvider handler={onSubMenuItemClick}>
				<ObjectListFlyout
					{...objectListFlyoutProps}
					itemMenuId={id}
					onClick={onObjectListFlyoutClick}
					onSubMenuItemClick={onObjectListSubMenuItemClick}
					isFlyoutOpen={isControlledFlyout}
					onOpenChange={onExpandFlyoutToggle}
					hasDragIndicator={isItemDraggable}
					triggerRef={draggableRef}
					isDragging={isDragging}
					dropIndicator={dropIndicator}
					visualContentRef={dropTargetRef}
				/>
				{maybeDragPreview}
			</ElementEventListenerProvider>
		</ItemWrapper>
	);
});

type ActionsOnHoverProps = {
	group?: ItemDragAndDropGroup;
	id: string;
	isInMoreMenu?: boolean;
	isItemDraggable: boolean;
	item: ButtonItemConfig | ExpandableItemConfig | LinkItemConfig;
};

// This cannot be a React function-component, otherwise the Link/Button/Expand.actionsOnHover prop will can never be undefined.
// When there are no actionsOnHover and the item is not draggable, actionsOnHover MUST return undefined, to avoid the
// elemAfter from being hidden unintentionally.
function getActionsOnHover({ item }: ActionsOnHoverProps) {
	return item.actionsOnHover ?? null;
}
